import { useMemo, useState, type DragEvent } from 'react';
import {
  Maximize2,
  Minimize2,
  Columns3,
  Pencil,
  MoreVertical,
  X,
  Plus,
  GripVertical,
} from 'lucide-react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import type { CourseSession } from '../../types/crm';

export type RosterSpreadsheetRow = {
  attendeeId: string;
  isVirtual?: boolean;
  isOrphaned?: boolean;
  name: string;
  company: string;
  sessionLanguage: string;
  preferredLanguage: string;
  status: string;
  paymentStatus: string;
  feeEuro: number;
  paidEuro: number;
  balanceEuro: number;
  processNumber: string;
  /** Safe Pass MH interpreter reminder (Yes/No). */
  mhFlag: string;
  invoiceNumber: string;
  invoicePaid: string;
  email: string;
  phone: string;
  bookedDate: string;
  bookedBy: string;
  bookedByUser: string;
  certificateSent: string;
  notes: string;
};

export type RosterSpreadsheetColumnKey = Exclude<keyof RosterSpreadsheetRow, 'attendeeId' | 'isVirtual' | 'isOrphaned'>;

type ColumnKey = RosterSpreadsheetColumnKey;

const ALL_COLUMNS: { key: ColumnKey; label: string; group: 'core' | 'money' | 'contact' | 'meta' }[] = [
  { key: 'name', label: 'Name', group: 'core' },
  { key: 'company', label: 'Company', group: 'core' },
  { key: 'paymentStatus', label: 'Payment', group: 'money' },
  { key: 'feeEuro', label: 'Individual Course Price', group: 'money' },
  { key: 'paidEuro', label: 'Paid', group: 'money' },
  { key: 'balanceEuro', label: 'Balance', group: 'money' },
  { key: 'processNumber', label: 'Process #', group: 'meta' },
  { key: 'mhFlag', label: 'MH', group: 'meta' },
  { key: 'invoiceNumber', label: 'Invoice #', group: 'money' },
  { key: 'invoicePaid', label: 'Inv. paid', group: 'money' },
  { key: 'sessionLanguage', label: 'Course lang.', group: 'meta' },
  { key: 'preferredLanguage', label: 'Pref. lang.', group: 'contact' },
  { key: 'status', label: 'Status', group: 'meta' },
  { key: 'email', label: 'Email', group: 'contact' },
  { key: 'phone', label: 'Phone', group: 'contact' },
  { key: 'bookedDate', label: 'Booked', group: 'meta' },
  { key: 'bookedBy', label: 'Booked by', group: 'meta' },
  { key: 'bookedByUser', label: 'CRM user', group: 'meta' },
  { key: 'certificateSent', label: 'Certificate sent', group: 'meta' },
  { key: 'notes', label: 'Notes', group: 'meta' },
];

const COLUMN_LABEL: Record<ColumnKey, string> = Object.fromEntries(
  ALL_COLUMNS.map((c) => [c.key, c.label])
) as Record<ColumnKey, string>;

const PRESET_COLUMNS: Record<string, ColumnKey[]> = {
  attendanceList: [
    'name',
    'company',
    'feeEuro',
    'paidEuro',
    'balanceEuro',
    'processNumber',
    'mhFlag',
    'sessionLanguage',
    'email',
    'phone',
    'bookedBy',
    'bookedByUser',
    'certificateSent',
    'notes',
  ],
  financial: ['name', 'company', 'feeEuro', 'paidEuro', 'balanceEuro', 'paymentStatus', 'invoiceNumber', 'invoicePaid'],
  contact: ['name', 'company', 'email', 'phone', 'preferredLanguage', 'paymentStatus'],
  all: ALL_COLUMNS.map((c) => c.key),
};

const PRESET_LABELS: Record<keyof typeof PRESET_COLUMNS, string> = {
  attendanceList: 'Attendance list',
  financial: 'Financial',
  contact: 'Contact',
  all: 'All',
};

const MONEY_KEYS = new Set<ColumnKey>(['feeEuro', 'paidEuro', 'balanceEuro']);
const STICKY_KEY: ColumnKey = 'name';
const EDITABLE_INLINE = new Set<ColumnKey>(['notes', 'processNumber', 'status', 'feeEuro']);
const READ_ONLY_ALWAYS = new Set<ColumnKey>([
  'balanceEuro',
  'invoiceNumber',
  'invoicePaid',
  'bookedDate',
  'bookedBy',
  'bookedByUser',
  'mhFlag',
  'sessionLanguage',
  'preferredLanguage',
  'email',
  'phone',
  'company',
  'name',
  'paymentStatus',
  'paidEuro',
]);

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'booked', label: 'Booked' },
  { value: 'completed', label: 'Attended' },
  { value: 'no_show', label: 'No-show' },
  { value: 'failed', label: 'Failed' },
  { value: 'waiting_list', label: 'Waiting list' },
  { value: 'cancelled', label: 'Removed' },
];

function formatCell(key: ColumnKey, value: RosterSpreadsheetRow[ColumnKey]): string {
  if (typeof value === 'number') return value.toFixed(2);
  if (key === 'paymentStatus') {
    if (value === 'paid') return 'Paid';
    if (value === 'half_paid') return 'Part paid';
    if (value === 'unpaid') return 'Unpaid';
  }
  if (key === 'status') {
    const opt = STATUS_OPTIONS.find((o) => o.value === value);
    if (opt) return opt.label;
  }
  return String(value ?? '');
}

function cellClass(key: ColumnKey, sticky: boolean): string {
  const base = sticky ? 'sticky left-0 z-10 border-r border-gray-200 bg-white' : '';
  if (MONEY_KEYS.has(key)) return `${base} min-w-[5.5rem] whitespace-nowrap px-2 py-1.5 text-right tabular-nums`;
  if (key === 'name') return `${base} min-w-[11rem] max-w-[16rem] px-2 py-1.5 font-medium text-gray-900`;
  if (key === 'company') return `${base} min-w-[8rem] max-w-[12rem] px-2 py-1.5`;
  if (key === 'notes') return `${base} min-w-[10rem] max-w-[18rem] px-2 py-1.5 whitespace-normal`;
  if (key === 'email') return `${base} min-w-[10rem] max-w-[14rem] px-2 py-1.5`;
  return `${base} min-w-[6.5rem] whitespace-nowrap px-2 py-1.5`;
}

export type RosterSpreadsheetActions = {
  onUpdateNotes?: (attendeeId: string, notes: string) => void;
  onUpdateProcessNumber?: (attendeeId: string, processNumber: string) => void;
  onUpdateStatus?: (attendeeId: string, status: string) => void;
  onUpdateFee?: (attendeeId: string, feeEuro: number) => void;
  onEditAttendee?: (attendeeId: string) => void;
  onRecordPayment?: (attendeeId: string) => void;
  onMarkAttended?: (attendeeId: string, attended: boolean) => void;
  onMarkNoShow?: (attendeeId: string) => void;
  onMarkFailed?: (attendeeId: string) => void;
  onRemove?: (attendeeId: string) => void;
};

type SessionRosterSpreadsheetProps = {
  session: CourseSession;
  rows: RosterSpreadsheetRow[];
  onBack: () => void;
  financeSummary?: { booked: number; capacity: number; collected: number; outstanding: number };
  canEdit?: boolean;
  /** Safe Pass attendance list includes the MH reminder column. */
  showMh?: boolean;
  actions?: RosterSpreadsheetActions;
};

function columnsForPreset(preset: keyof typeof PRESET_COLUMNS, showMh: boolean): ColumnKey[] {
  const cols = [...PRESET_COLUMNS[preset]];
  if (showMh) return cols;
  return cols.filter((key) => key !== 'mhFlag');
}

export function SessionRosterSpreadsheet({
  session,
  rows,
  onBack,
  financeSummary,
  canEdit = true,
  showMh = false,
  actions,
}: SessionRosterSpreadsheetProps) {
  const [visibleOrder, setVisibleOrder] = useState<ColumnKey[]>(() => columnsForPreset('attendanceList', showMh));
  const [preset, setPreset] = useState<keyof typeof PRESET_COLUMNS>('attendanceList');
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [actionsOpenId, setActionsOpenId] = useState<string | null>(null);
  const [dragKey, setDragKey] = useState<ColumnKey | null>(null);

  const activeColumns = useMemo(
    () => visibleOrder.map((key) => ALL_COLUMNS.find((c) => c.key === key)!).filter(Boolean),
    [visibleOrder]
  );

  const hiddenColumns = useMemo(() => {
    const visible = new Set(visibleOrder);
    return ALL_COLUMNS.filter((c) => !visible.has(c.key) && (showMh || c.key !== 'mhFlag'));
  }, [visibleOrder, showMh]);

  const applyPreset = (next: keyof typeof PRESET_COLUMNS) => {
    setPreset(next);
    setVisibleOrder(columnsForPreset(next, showMh));
  };

  const removeColumn = (key: ColumnKey) => {
    if (key === STICKY_KEY) return;
    setPreset('all');
    setVisibleOrder((prev) => prev.filter((k) => k !== key));
  };

  const addColumn = (key: ColumnKey) => {
    setPreset('all');
    setVisibleOrder((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const onDragStart = (key: ColumnKey) => (e: DragEvent) => {
    if (key === STICKY_KEY) {
      e.preventDefault();
      return;
    }
    setDragKey(key);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (key: ColumnKey) => (e: DragEvent) => {
    e.preventDefault();
    if (!dragKey || dragKey === key || key === STICKY_KEY) return;
  };

  const onDrop = (targetKey: ColumnKey) => (e: DragEvent) => {
    e.preventDefault();
    if (!dragKey || dragKey === targetKey || targetKey === STICKY_KEY) {
      setDragKey(null);
      return;
    }
    setPreset('all');
    setVisibleOrder((prev) => {
      const next = prev.filter((k) => k !== dragKey);
      const idx = next.indexOf(targetKey);
      if (idx < 0) return [...next, dragKey];
      next.splice(idx, 0, dragKey);
      return next;
    });
    setDragKey(null);
  };

  const exportCsv = () => {
    const header = activeColumns.map((c) => c.label);
    const body = rows.map((r) => activeColumns.map((c) => formatCell(c.key, r[c.key])));
    const csv = [header, ...body]
      .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `roster-${session.courseName.replace(/\s+/g, '-')}.csv`);
  };

  const exportXlsx = () => {
    const sheetRows = rows.map((r) => {
      const out: Record<string, string | number> = {};
      activeColumns.forEach((c) => {
        out[c.label] = r[c.key] as string | number;
      });
      return out;
    });
    const ws = XLSX.utils.json_to_sheet(sheetRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Roster');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf]), `roster-${session.courseName.replace(/\s+/g, '-')}.xlsx`);
  };

  const shellClass = fullscreen
    ? 'fixed inset-2 z-[60] flex flex-col overflow-hidden rounded-xl border border-gray-300 bg-white shadow-2xl sm:inset-3'
    : 'flex min-h-[calc(96vh-11rem)] flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white';

  const showActionsCol = editMode && canEdit && Boolean(actions);
  const colSpan = activeColumns.length + (showActionsCol ? 1 : 0);

  const renderEditableCell = (r: RosterSpreadsheetRow, key: ColumnKey) => {
    const raw = r[key];
    const display = formatCell(key, raw);
    const disabled = r.isVirtual || r.isOrphaned;

    if (!editMode || !canEdit || READ_ONLY_ALWAYS.has(key) || !EDITABLE_INLINE.has(key) || !actions) {
      return display || '—';
    }

    if (key === 'status') {
      return (
        <select
          className="w-full min-w-[6rem] rounded border border-gray-200 bg-white px-1 py-0.5 text-xs"
          value={r.status || 'booked'}
          disabled={disabled}
          onChange={(e) => actions.onUpdateStatus?.(r.attendeeId, e.target.value)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    if (key === 'feeEuro') {
      return (
        <input
          type="number"
          step="0.01"
          min="0"
          className="w-20 rounded border border-gray-200 bg-white px-1 py-0.5 text-right text-xs tabular-nums"
          defaultValue={Number(r.feeEuro || 0).toFixed(2)}
          disabled={disabled}
          onBlur={(e) => {
            const next = Number(e.target.value);
            if (!Number.isFinite(next) || Math.abs(next - r.feeEuro) < 0.005) return;
            actions.onUpdateFee?.(r.attendeeId, Math.max(0, next));
          }}
        />
      );
    }

    if (key === 'notes') {
      return (
        <input
          type="text"
          maxLength={150}
          className="w-full min-w-[8rem] rounded border border-gray-200 bg-white px-1 py-0.5 text-xs"
          defaultValue={r.notes}
          key={`${r.attendeeId}-notes-${r.notes}`}
          disabled={disabled}
          placeholder="Note"
          onBlur={(e) => {
            const next = e.target.value.trim().slice(0, 50);
            if (next !== r.notes) actions.onUpdateNotes?.(r.attendeeId, next);
          }}
        />
      );
    }

    if (key === 'processNumber') {
      return (
        <input
          type="text"
          className="w-full min-w-[6rem] rounded border border-gray-200 bg-white px-1 py-0.5 text-xs"
          defaultValue={r.processNumber}
          key={`${r.attendeeId}-proc-${r.processNumber}`}
          disabled={disabled}
          placeholder="Process #"
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next !== r.processNumber) actions.onUpdateProcessNumber?.(r.attendeeId, next);
          }}
        />
      );
    }

    return display || '—';
  };

  return (
    <div className={shellClass}>
      <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">Roster spreadsheet · {session.courseName}</p>
            <p className="text-xs text-gray-500">
              {rows.length} attendees · scroll horizontally for more columns
              {editMode ? (
                <span className="ml-2 font-medium text-blue-700">Editing on — same actions as card view</span>
              ) : null}
            </p>
            {financeSummary && (
              <p className="mt-1 text-xs text-gray-600">
                <span className="font-semibold text-gray-800">
                  {financeSummary.booked}/{financeSummary.capacity} booked
                </span>
                <span className="mx-1.5 text-gray-300">·</span>
                <span className="text-emerald-700">€{financeSummary.collected.toFixed(2)} collected</span>
                <span className="mx-1.5 text-gray-300">·</span>
                <span className="text-red-700">€{financeSummary.outstanding.toFixed(2)} outstanding</span>
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-gray-200 bg-white p-0.5">
              {(Object.keys(PRESET_COLUMNS) as Array<keyof typeof PRESET_COLUMNS>).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                    preset === key ? 'bg-slate-800 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => applyPreset(key)}
                >
                  {PRESET_LABELS[key]}
                </button>
              ))}
            </div>
            {canEdit ? (
              <button
                type="button"
                className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
                  editMode
                    ? 'border-blue-400 bg-blue-50 text-blue-800 ring-1 ring-blue-200'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setEditMode((v) => !v)}
                title={editMode ? 'Turn editing off' : 'Turn editing on'}
              >
                <Pencil className={`h-3.5 w-3.5 ${editMode ? 'fill-blue-200' : ''}`} />
                {editMode ? 'Editing' : 'Edit'}
              </button>
            ) : null}
            <button
              type="button"
              className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium ${
                showColumnPicker
                  ? 'border-slate-400 bg-slate-100 text-slate-900'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setShowColumnPicker((v) => !v)}
            >
              <Columns3 className="h-3.5 w-3.5" />
              Columns
            </button>
            <button
              type="button"
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
              onClick={exportCsv}
            >
              CSV
            </button>
            <button
              type="button"
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
              onClick={exportXlsx}
            >
              Excel
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
              onClick={() => setFullscreen((v) => !v)}
              title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900"
              onClick={onBack}
            >
              Card view
            </button>
          </div>
        </div>

        {showColumnPicker && (
          <div className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-white p-3">
            <div>
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                Visible columns — drag to reorder, X to hide
              </p>
              <div className="flex flex-wrap gap-1.5">
                {visibleOrder.map((key) => (
                  <span
                    key={key}
                    draggable={key !== STICKY_KEY}
                    onDragStart={onDragStart(key)}
                    onDragOver={onDragOver(key)}
                    onDrop={onDrop(key)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
                      key === STICKY_KEY
                        ? 'border-slate-300 bg-slate-100 text-slate-600'
                        : 'cursor-grab border-blue-200 bg-blue-50 text-blue-900 active:cursor-grabbing'
                    } ${dragKey === key ? 'opacity-50' : ''}`}
                  >
                    {key !== STICKY_KEY ? <GripVertical className="h-3 w-3 text-blue-400" /> : null}
                    {COLUMN_LABEL[key]}
                    {key !== STICKY_KEY ? (
                      <button
                        type="button"
                        className="rounded-full p-0.5 hover:bg-blue-100"
                        onClick={() => removeColumn(key)}
                        aria-label={`Hide ${COLUMN_LABEL[key]}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    ) : null}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                Available — click + to add to view
              </p>
              <div className="flex flex-wrap gap-1.5">
                {hiddenColumns.length === 0 ? (
                  <span className="text-xs text-gray-400">All columns are visible</span>
                ) : (
                  hiddenColumns.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => addColumn(c.key)}
                      className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-900"
                    >
                      <Plus className="h-3 w-3" />
                      {c.label}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto" onClick={() => setActionsOpenId(null)}>
        <table className="min-w-full border-collapse text-sm">
          <thead className="sticky top-0 z-20 bg-gray-100 text-[11px] font-bold uppercase tracking-wide text-gray-600 shadow-sm">
            <tr>
              {activeColumns.map((c) => (
                <th
                  key={c.key}
                  className={`border-b border-gray-200 py-2 ${
                    c.key === STICKY_KEY ? 'sticky left-0 z-30 border-r border-gray-200 bg-gray-100' : ''
                  } ${MONEY_KEYS.has(c.key) ? 'px-2 text-right' : 'px-2 text-left'}`}
                >
                  {c.label}
                </th>
              ))}
              {showActionsCol ? (
                <th className="sticky right-0 z-30 border-b border-l border-gray-200 bg-gray-100 px-2 py-2 text-left">
                  Actions
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr
                key={r.attendeeId || idx}
                className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'} hover:bg-blue-50/50`}
              >
                {activeColumns.map((c) => {
                  const sticky = c.key === STICKY_KEY;
                  const raw = r[c.key];
                  const balanceNegative = c.key === 'balanceEuro' && typeof raw === 'number' && raw < -0.005;
                  const balancePositive = c.key === 'balanceEuro' && typeof raw === 'number' && raw > 0.005;
                  return (
                    <td
                      key={c.key}
                      className={`${cellClass(c.key, sticky)} ${
                        sticky ? (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60') : ''
                      } ${balanceNegative ? 'font-semibold text-violet-700' : ''} ${
                        balancePositive ? 'font-semibold text-red-700' : ''
                      } ${c.key === 'paymentStatus' && raw === 'paid' ? 'text-emerald-700' : ''} ${
                        c.key === 'paymentStatus' && raw === 'half_paid' ? 'text-amber-700' : ''
                      } ${c.key === 'paymentStatus' && raw === 'unpaid' ? 'text-red-600' : ''}`}
                    >
                      {renderEditableCell(r, c.key)}
                    </td>
                  );
                })}
                {showActionsCol && actions ? (
                  <td
                    className={`sticky right-0 z-10 border-l border-gray-200 px-2 py-1.5 ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="relative">
                      <button
                        type="button"
                        className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        onClick={() => setActionsOpenId((id) => (id === r.attendeeId ? null : r.attendeeId))}
                        aria-label="Row actions"
                      >
                        <MoreVertical className="h-3.5 w-3.5" />
                      </button>
                      {actionsOpenId === r.attendeeId ? (
                        <div className="absolute right-0 z-40 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 text-xs shadow-lg">
                          <button
                            type="button"
                            className="block w-full px-3 py-1.5 text-left hover:bg-gray-50"
                            onClick={() => {
                              actions.onEditAttendee?.(r.attendeeId);
                              setActionsOpenId(null);
                            }}
                          >
                            Edit attendee
                          </button>
                          <button
                            type="button"
                            className="block w-full px-3 py-1.5 text-left hover:bg-gray-50"
                            onClick={() => {
                              actions.onRecordPayment?.(r.attendeeId);
                              setActionsOpenId(null);
                            }}
                          >
                            Payment
                          </button>
                          <button
                            type="button"
                            className="block w-full px-3 py-1.5 text-left hover:bg-gray-50"
                            disabled={r.isVirtual}
                            onClick={() => {
                              actions.onMarkAttended?.(r.attendeeId, r.status !== 'completed');
                              setActionsOpenId(null);
                            }}
                          >
                            {r.status === 'completed' ? 'Unmark attended' : 'Mark attended'}
                          </button>
                          <button
                            type="button"
                            className="block w-full px-3 py-1.5 text-left hover:bg-gray-50"
                            disabled={r.isVirtual}
                            onClick={() => {
                              actions.onMarkNoShow?.(r.attendeeId);
                              setActionsOpenId(null);
                            }}
                          >
                            No-show
                          </button>
                          <button
                            type="button"
                            className="block w-full px-3 py-1.5 text-left hover:bg-gray-50"
                            disabled={r.isVirtual}
                            onClick={() => {
                              actions.onMarkFailed?.(r.attendeeId);
                              setActionsOpenId(null);
                            }}
                          >
                            Failed
                          </button>
                          <button
                            type="button"
                            className="block w-full px-3 py-1.5 text-left text-red-700 hover:bg-red-50"
                            onClick={() => {
                              actions.onRemove?.(r.attendeeId);
                              setActionsOpenId(null);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="px-4 py-8 text-center text-sm text-gray-500">
                  No active attendees on this roster.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}